// materialCatalog.js — every material and test in the lab's NABL scope (Certificate TC-16994, valid 28/10/2025 – 27/10/2029).
// Source: Scope of Accreditation, permanent testing (pp. 1-24) and site testing (pp. 25-29).
//
// TO ADD A NEW MATERIAL : add it to MATERIALS (pick a group), then add/extend rows in ROWS.
// TO GO LIVE WITH A TEST: build its form + exporter, register it in testRegistry.js (id = 'xyz'), then put 'xyz'
//                         in the 4th slot of every row it covers. Until then the row shows as "Coming soon".
//
// Notes: soil and clay stay two separate choices (the grain-size test needs to know it is clay). Every scope row
// that says "Soil" or "Clay & Soil" is attached to both. MDD + OMC (and LL + PL + PI) are one test in the scope
// and one form in the portal, so they are merged into a single row.

export const GROUPS = [
  'Soil, rocks & road base',
  'Aggregates',
  'Cement & concrete',
  'Bricks, blocks & tiles',
  'Bitumen & bituminous mix',
  'Road marking & signage',
  'Steel',
  'Pavement & structures (site)',
];

export const MATERIALS = [
  // Soil, rocks & road base
  { id: 'soil', label: 'Soil', group: 0 },
  { id: 'clay', label: 'Clay', group: 0 },
  { id: 'bentonite', label: 'Bentonite', group: 0 },
  { id: 'flyash', label: 'Fly Ash / Pond Ash', group: 0 },
  { id: 'rock', label: 'Rock', group: 0 },
  { id: 'gsb', label: 'Granular Sub-Base (GSB)', group: 0 },
  { id: 'wmm', label: 'Wet Mix Macadam (WMM)', group: 0 },
  { id: 'wbm', label: 'Water Bound Macadam (WBM)', group: 0 },
  { id: 'ctsb', label: 'Cement Treated Sub Base / Base', group: 0 },
  // Aggregates
  { id: 'coarse_agg', label: 'Coarse Aggregate', group: 1 },
  { id: 'ballast', label: 'Ballast Material', group: 1 },
  { id: 'fine_agg', label: 'Fine Aggregate (Sand, Stone Dust)', group: 1 },
  { id: 'bit_agg', label: 'Aggregate for Bituminous Mix', group: 1 },
  // Cement & concrete
  { id: 'cement', label: 'Cement (OPC / PPC)', group: 2 },
  { id: 'fresh_conc', label: 'Fresh Concrete', group: 2 },
  { id: 'hard_conc', label: 'Hardened Concrete (Cube / Core)', group: 2 },
  { id: 'acc_conc', label: 'Accelerated Cured Concrete', group: 2 },
  // Bricks, blocks & tiles
  { id: 'bcb', label: 'Burnt Clay Building Bricks', group: 3 },
  { id: 'bcfa', label: 'Burnt Clay Fly Ash Building Bricks', group: 3 },
  { id: 'sewer', label: 'Sewer Bricks', group: 3 },
  { id: 'aac', label: 'Autoclaved Cellular (Aerated) Concrete Blocks', group: 3 },
  { id: 'paver', label: 'Concrete Paver Block / Interlocking Block', group: 3 },
  { id: 'tiles', label: 'Cement Concrete Flooring Tiles', group: 3 },
  // Bitumen
  { id: 'paving_bit', label: 'Paving Bitumen', group: 4 },
  { id: 'mod_bit', label: 'Modified Bitumen', group: 4 },
  { id: 'bit_emul', label: 'Bitumen Emulsion', group: 4 },
  { id: 'bit_mix', label: 'Bituminous Mix (DBM / BM / BC / SDBC / SMA)', group: 4 },
  // Road marking
  { id: 'tp_paint', label: 'Thermo-Plastic Paint', group: 5 },
  { id: 'tp_marking', label: 'Thermo-Plastic Road Marking Material', group: 5 },
  { id: 'retro_sheet', label: 'Retro Reflective Sheeting', group: 5 },
  // Steel
  { id: 'tmt', label: 'TMT Steel Bar', group: 6 },
  // Pavement & structures (site)
  { id: 'flex_pave', label: 'Flexible Pavement', group: 7 },
  { id: 'rigid_pave', label: 'Rigid Pavement', group: 7 },
  { id: 'pc_flex', label: 'Pavement Condition (Flexible)', group: 7 },
  { id: 'pc_rigid', label: 'Pavement Condition (Rigid)', group: 7 },
  { id: 'rcc', label: 'Reinforced Concrete Structures', group: 7 },
];

const SC = ['soil', 'clay'];
const ROAD_BASE = ['gsb', 'wmm', 'wbm'];
const BRICKS3 = ['bcb', 'bcfa', 'sewer'];
const PAVE = ['flex_pave', 'rigid_pave'];
const LCMS = 'IRC 82 (by LCMS - Network Survey Vehicle)';
const LCMS_R = 'IRC SP 83 (by LCMS - Network Survey Vehicle)';

// [materials, test, method / clause, flag]   flag: 'site' = site test · any other string = id of the finished test (testRegistry.js)
export const ROWS = [
  // ───────────── Soil & rocks ─────────────
  [SC, 'Liquid limit, plastic limit & plasticity index', 'IS 2720 (Part 5)', 'llpl'],
  [SC, 'Grain size by sieve analysis (4.75 mm to 0.075 mm)', 'IS 2720 (Part 4)', 'gsa'],
  [SC, 'Free swell index', 'IS 2720 (Part 40)', 'fsi'],
  [SC, 'Compaction – MDD / OMC (heavy)', 'IS 2720 (Part 8)', 'mdd'],
  [SC, 'California bearing ratio (CBR)', 'IS 2720 (Part 16)', 'cbr'],
  [SC, 'Field dry density (core cutter)', 'IS 2720 (Part 29)', 'fdd', 'site'],
  [SC, 'Compaction – MDD / OMC (light)', 'IS 2720 (Part 7)'],
  [SC, 'California bearing ratio (CBR)', 'AASHTO T 193'],
  [SC, 'Unconfined compressive strength, cohesion (C)', 'IS 2720 (Part 10)'],
  [SC, 'UU triaxial compression, angle of friction', 'IS 2720 (Part 11)'],
  [SC, 'UU triaxial compression, cohesion (C)', 'IS 2720 (Part 11)'],
  [SC, 'Water content', 'IS 2720 (Part 2)'],
  [SC, 'Coefficient of compressibility (av)', 'IS 2720 (Part 15)'],
  [SC, 'Coefficient of consolidation (Cv)', 'IS 2720 (Part 15)'],
  [SC, 'Consolidation test, compression index (Cc)', 'IS 2720 (Part 15)'],
  [SC, 'Direct shear test, angle of friction', 'IS 2720 (Part 13)'],
  [SC, 'Direct shear test, cohesion value', 'IS 2720 (Part 13)'],
  [SC, 'Grain size analysis – hydrometer (passing 75 micron)', 'IS 2720 (Part 4) (by Hydrometer)'],
  [SC, 'Permeability (falling head method)', 'IS 2720 (Part 17)'],
  [SC, 'Specific gravity – coarse grained soil', 'IS 2720 (Part 3/Sec 2)'],
  [SC, 'Specific gravity – fine grained soil', 'IS 2720 (Part 3/Sec 1)'],
  [SC, 'Dynamic cone penetrometer', 'ASTM D6951', 'site'],
  [SC, 'Field dry density (sand replacement)', 'IS 2720 (Part 28)', 'site'],
  [SC, 'Strain modulus (EV2)', 'DIN 18134', 'site'],

  [['bentonite'], 'Liquid limit & plastic limit', 'IS 2720 (Part 5)', 'llpl'],
  [['bentonite'], 'Fineness (150 micron) – dry sieve', 'IS 6186'],
  [['bentonite'], 'Fineness (150 micron) – wet sieve', 'IS 6186'],
  [['bentonite'], 'Fineness (75 micron) – dry sieve', 'IS 6186'],
  [['bentonite'], 'Fineness (45 micron) – wet sieve', 'IS 6186'],
  [['bentonite'], 'Moisture content', 'IS 6186'],
  [['bentonite'], 'pH', 'IS 6186'],
  [['bentonite'], 'Sand content', 'IS 6186'],
  [['bentonite'], 'Swelling', 'IS 6186'],

  [['ctsb', ...ROAD_BASE], 'Liquid limit, plastic limit & plasticity index', 'IS 2720 (Part 5)', 'llpl'],
  [['ctsb', ...ROAD_BASE, 'flyash'], 'Compaction – MDD / OMC (heavy)', 'IS 2720 (Part 8)', 'mdd'],
  [['flyash'], 'Compaction – MDD / OMC (light)', 'IS 2720 (Part 7)'],
  [['gsb'], 'California bearing ratio (CBR)', 'IS 2720 (Part 16)', 'cbr'],
  [['flyash'], 'Direct shear test, angle of friction', 'IS 2720 (Part 13)'],
  [['flyash'], 'Direct shear test, cohesion value', 'IS 2720 (Part 13)'],
  [['flyash'], 'Permeability (falling head method)', 'IS 2720 (Part 17)'],
  [['flyash'], 'Specific gravity', 'IS 2720 (Part 3/Sec 2)'],
  [['flyash'], 'Wet sieving (retained on 45 micron)', 'IS 1727'],
  [['flyash'], 'Dry sieving (sieve analysis 45 micron)', 'IS 1727'],
  [['gsb', 'wmm'], 'Field dry density (sand replacement)', 'IS 2720 (Part 28)', 'site'],

  [['rock'], 'Density', 'IS 13030'],
  [['rock'], 'Porosity', 'IS 13030'],
  [['rock'], 'Water content', 'IS 13030'],
  [['rock'], 'Unconfined compressive strength', 'IS 9143'],

  // Cement treated base
  [['ctsb'], 'Durability – freezing and thawing', 'IS 4332 (Part 4)'],
  [['ctsb'], 'Durability – wetting and drying', 'IS 4332 (Part 4)'],
  [['ctsb'], 'Elastic modulus', 'IRC SP 89'],
  [['ctsb'], 'Unconfined compressive strength', 'IS 4332 (Part 5)'],
  [['ctsb'], 'Impact value (coarse aggregate)', 'IS 2386 (Part 4)'],
  [['ctsb'], 'Wet impact value (coarse aggregate)', 'IS 5640'],

  // WMM / GSB / WBM aggregate tests
  [ROAD_BASE, 'Elongation index', 'IS 2386 (Part 1)'],
  [ROAD_BASE, 'Flakiness index', 'IS 2386 (Part 1)'],
  [ROAD_BASE, 'Impact value', 'IS 2386 (Part 4)'],
  [ROAD_BASE, 'Impact value (wet condition)', 'IS 5640'],
  [ROAD_BASE, 'Los Angeles abrasion value', 'IS 2386 (Part 4)'],
  [ROAD_BASE, 'Sieve analysis (75 mm to 0.075 mm)', 'IS 2386 (Part 1)'],
  [ROAD_BASE, 'Water absorption', 'IS 2386 (Part 3)'],

  // ───────────── Aggregates ─────────────
  [['coarse_agg'], '10 % fines value', 'IS 2386 (Part 4)'],
  [['coarse_agg'], 'Aggregate crushing value', 'IS 2386 (Part 4)'],
  [['coarse_agg'], 'Angularity number', 'IS 2386 (Part 1)'],
  [['coarse_agg'], 'Elongation index', 'IS 2386 (Part 1)'],
  [['coarse_agg'], 'Flakiness index', 'IS 2386 (Part 1)'],
  [['coarse_agg'], 'Material finer than 75 micron IS sieve', 'IS 2386 (Part 1)'],
  [['coarse_agg'], 'Percentage of voids', 'IS 2386 (Part 3)'],
  [['coarse_agg'], 'Polished stone value', 'IS 2386 (Part 3)'],
  [['coarse_agg'], 'Skid resistance test', 'ASTM E303'],
  [['coarse_agg'], 'Soundness (magnesium sulphate)', 'IS 2386 (Part 5)'],
  [['coarse_agg'], 'Soundness (sodium sulphate)', 'IS 2386 (Part 5)'],
  [['coarse_agg'], 'Striping value', 'IS 6241'],
  [['coarse_agg', 'ballast'], 'Aggregate impact value', 'IS 2386 (Part 4)'],
  [['coarse_agg', 'ballast'], 'Bulk density', 'IS 2386 (Part 3)'],
  [['coarse_agg', 'ballast'], 'Los Angeles abrasion value', 'IS 2386 (Part 4)'],
  [['coarse_agg', 'ballast'], 'Sieve analysis (125 mm to 2.36 mm)', 'IS 2386 (Part 1)'],
  [['coarse_agg', 'ballast'], 'Specific gravity', 'IS 2386 (Part 3)'],
  [['coarse_agg', 'ballast'], 'Water absorption', 'IS 2386 (Part 3)'],
  [['coarse_agg', 'ballast'], 'Wet impact value', 'IS 5640'],

  [['fine_agg'], 'Sand equivalent value', 'IS 2720 (Part 37)'],
  [['fine_agg'], 'Surface moisture', 'IS 2386 (Part 3)'],
  [['fine_agg'], 'Bulk density', 'IS 2386 (Part 3)'],
  [['fine_agg'], 'Deleterious material (clay lumps)', 'IS 2386 (Part 2)'],
  [['fine_agg'], 'Materials finer than 75 micron IS sieve', 'IS 2386 (Part 1)'],
  [['fine_agg'], 'Sieve analysis (10 mm to 0.150 mm)', 'IS 2386 (Part 1)'],
  [['fine_agg'], 'Silt content', 'CPWD Volume 1 (Clause 3.1.3.2)'],
  [['fine_agg'], 'Soundness (magnesium sulphate)', 'IS 2386 (Part 5)'],
  [['fine_agg'], 'Soundness (sodium sulphate)', 'IS 2386 (Part 5)'],
  [['fine_agg'], 'Specific gravity', 'IS 2386 (Part 3)'],
  [['fine_agg'], 'Water absorption', 'IS 2386 (Part 3)'],
  [['fine_agg'], 'Bulking', 'CPWD Volume 1 (Clause 3.1.3.5)', 'site'],
  [['fine_agg'], 'Bulking', 'IS 2386 (Part 3)', 'site'],

  [['bit_agg'], 'Sieve analysis (45 mm to 0.075 mm)', 'IS 2386 (Part 1)'],

  // ───────────── Cement & concrete ─────────────
  [['cement'], 'Compressive strength (72 ± 1 h, 168 ± 2 h & 672 ± 4 h)', 'IS 4031 (Part 6)'],
  [['cement'], 'Consistency', 'IS 4031 (Part 4)'],
  [['cement'], 'Density', 'IS 4031 (Part 11)'],
  [['cement'], 'Initial setting time', 'IS 4031 (Part 5)'],
  [['cement'], 'Final setting time', 'IS 4031 (Part 5)'],
  [['cement'], 'Fineness (dry sieving)', 'IS 4031 (Part 1)'],
  [['cement'], 'Fineness (Blaine\'s air permeability)', 'IS 4031 (Part 2)'],
  [['cement'], 'Soundness (autoclave method)', 'IS 4031 (Part 3)'],
  [['cement'], 'Soundness (Le-Chatelier method)', 'IS 4031 (Part 3)'],

  [['fresh_conc'], 'Air content', 'IS 1199 (Part 4)'],
  [['fresh_conc'], 'Initial setting time', 'IS 1199 (Part 7)'],
  [['fresh_conc'], 'Final setting time', 'IS 1199 (Part 7)'],
  [['fresh_conc'], 'Slump test', 'IS 1199 (Part 2)'],

  [['hard_conc'], 'Density', 'IS 516 (Part 2/Sec 1)'],
  [['hard_conc'], 'Flexural strength', 'IS 516 (Part 1/Sec 1)'],
  [['hard_conc'], 'Compressive strength – core', 'IS 516 (Part 4)'],
  [['hard_conc'], 'Compressive strength – cube', 'IS 516 (Part 1/Sec 1)'],

  [['acc_conc'], 'Compressive strength', 'IS 9013 (Boiling Water Method)'],

  // ───────────── Bricks, blocks & tiles ─────────────
  [['bcb'], 'Dimension (length)', 'IS 1077'],
  [['bcb'], 'Dimension (width)', 'IS 1077'],
  [['bcb'], 'Dimension (height)', 'IS 1077'],
  [['bcfa'], 'Dimension (length)', 'IS 13757'],
  [['bcfa'], 'Dimension (width)', 'IS 13757'],
  [['bcfa'], 'Dimension (height)', 'IS 13757'],
  [BRICKS3, 'Water absorption', 'IS 3495 (Part 2)'],
  [BRICKS3, 'Compressive strength', 'IS 3495 (Part 1)'],
  [BRICKS3, 'Efflorescence', 'IS 3495 (Part 3)'],

  [['aac'], 'Bulk density', 'IS 6441 (Part 1)'],
  [['aac'], 'Compressive strength', 'IS 6441 (Part 5)'],
  [['aac'], 'Moisture content', 'IS 6441 (Part 1)'],
  [['aac'], 'Dimension (length)', 'IS 2185 (Part 3)'],
  [['aac'], 'Dimension (thickness)', 'IS 2185 (Part 3)'],
  [['aac'], 'Dimension (width)', 'IS 2185 (Part 3)'],

  [['paver'], 'Compressive strength', 'IS 15658'],
  [['paver'], 'Dimension (length)', 'IS 15658'],
  [['paver'], 'Dimension (thickness)', 'IS 15658'],
  [['paver'], 'Dimension (width)', 'IS 15658'],
  [['paver'], 'Water absorption', 'IS 15658'],

  [['tiles'], 'Dimension (length)', 'IS 1237'],
  [['tiles'], 'Dimension (thickness)', 'IS 1237'],
  [['tiles'], 'Dimension (width)', 'IS 1237'],
  [['tiles'], 'Flatness', 'IS 1237'],
  [['tiles'], 'Perpendicularity', 'IS 1237'],
  [['tiles'], 'Water absorption', 'IS 1237'],

  // ───────────── Bitumen & bituminous mix ─────────────
  [['paving_bit'], 'Absolute viscosity', 'IS 1206 (Part 2)'],
  [['paving_bit'], 'Ductility', 'IS 1208'],
  [['paving_bit'], 'Flash point', 'IS 1209'],
  [['paving_bit'], 'Kinematic viscosity (at 135 °C)', 'IS 1206 (Part 3)'],
  [['paving_bit'], 'Penetration', 'IS 1203'],
  [['paving_bit'], 'Softening point', 'IS 1205'],
  [['paving_bit'], 'Solubility in trichloroethylene', 'IS 1216'],
  [['paving_bit'], 'Specific gravity', 'IS 1202'],
  [['paving_bit'], 'Viscosity ratio at 60 °C', 'IS 1206 (Part 2)'],

  [['mod_bit'], 'Elastic recovery of half thread in ductilometer at 15 °C', 'IS 15462 (Annex A)'],
  [['mod_bit'], 'Separation (difference in softening points, R&B)', 'IS 15462 (Annex C)'],
  [['mod_bit'], 'Flash point', 'IS 1209'],
  [['mod_bit'], 'Softening point', 'IS 1205'],

  [['bit_emul'], 'Ductility at 27 °C', 'IS 1208 (Part 1)'],
  [['bit_emul'], 'Miscibility with water', 'IS 8887'],
  [['bit_emul'], 'Penetration (25 °C, 100 g, 5 s)', 'IS 1203'],
  [['bit_emul'], 'Residue by evaporation', 'IS 8887'],
  [['bit_emul'], 'Residue on 600 micron', 'IS 8887'],
  [['bit_emul'], 'Solubility in trichloroethylene', 'IS 1216'],
  [['bit_emul'], 'Viscosity (Saybolt viscometer)', 'IS 3117'],

  [['bit_mix'], 'Air voids', 'MS-2 7th Edition'],
  [['bit_mix'], 'Binder content (centrifuge extraction)', 'ASTM D2172'],
  [['bit_mix'], 'Binder content (centrifuge extraction)', 'IRC SP 11'],
  [['bit_mix'], 'Binder content (centrifuge extraction)', 'IS 19008'],
  [['bit_mix'], 'Bulk density', 'ASTM D2726'],
  [['bit_mix'], 'Indirect tensile strength (ITS)', 'ASTM D6931'],
  [['bit_mix'], 'Marshall flow', 'AASHTO T 245'],
  [['bit_mix'], 'Marshall flow', 'IS 17127'],
  [['bit_mix'], 'Marshall quotient', 'MS-2 7th Edition'],
  [['bit_mix'], 'Marshall stability', 'AASHTO T 245'],
  [['bit_mix'], 'Marshall stability', 'IS 17127'],
  [['bit_mix'], 'Theoretical maximum specific gravity (Gmm)', 'ASTM D2041'],
  [['bit_mix'], 'Voids filled with bitumen (VFB)', 'MS-2 7th Edition'],
  [['bit_mix'], 'Voids in mineral aggregate (VMA)', 'MS-2 7th Edition'],
  [['bit_mix'], 'Water sensitivity – retained tensile strength', 'AASHTO 283'],

  // ───────────── Road marking & signage ─────────────
  [['tp_paint'], 'Drying time', 'AASHTO M 249'],
  [['tp_paint'], 'Retro-reflectivity (road marking), day visibility – Qd', 'IRC 35', 'site'],
  [['tp_paint'], 'Retro-reflectivity, night visibility – RL', 'IRC 35', 'site'],
  [['tp_marking'], 'Gradation of glass beads (Type 1 & 2)', 'BS 3262 (Part 1)'],
  [['tp_marking'], 'Skid resistance test', 'IRC 35', 'site'],
  [['retro_sheet'], 'Retro-reflectivity (entrance angle -4°, observation angle 0.2°, 0.5° & 1.0°)', 'IRC 67', 'site'],

  // ───────────── Steel ─────────────
  [['tmt'], 'Tensile strength', 'IS 1608 (Part 1)'],
  [['tmt'], 'Yield stress', 'IS 1608 (Part 1)'],
  [['tmt'], '% Elongation', 'IS 1608 (Part 1)'],
  [['tmt'], 'TS / YS ratio', 'IS 1786'],
  [['tmt'], 'Bend test', 'IS 1599'],
  [['tmt'], 'Re-bend test', 'IS 1786'],
  [['tmt'], 'Cross sectional area', 'IS 1786'],
  [['tmt'], 'Mass per metre', 'IS 1786'],

  // ───────────── Pavement & structures (site) ─────────────
  [['flex_pave'], 'Deflection (falling weight deflectometer)', 'IRC 115', 'site'],
  [['flex_pave'], 'Elastic modulus of pavement layer', 'IRC 115', 'site'],
  [['rigid_pave'], 'Deflection (falling weight deflectometer)', 'IRC 117', 'site'],
  [['rigid_pave'], 'Elastic modulus of pavement layer', 'IRC 117', 'site'],
  [['rigid_pave'], 'Surface texture depth', 'MoRTH 5th Revision', 'site'],
  [PAVE, 'Roughness index / International Roughness Index', 'IRC SP 16 (by Network Survey Vehicle)', 'site'],
  [PAVE, 'Skid resistance test', 'ASTM E303', 'site'],

  [['pc_flex'], 'Cracking (depth measurement)', LCMS, 'site'],
  [['pc_flex'], 'Cracking (length measurement)', LCMS, 'site'],
  [['pc_flex'], 'Cracking (width measurement)', LCMS, 'site'],
  [['pc_flex'], 'Pothole (area)', LCMS, 'site'],
  [['pc_flex'], 'Pothole (depth measurement)', LCMS, 'site'],
  [['pc_flex'], 'Rutting', LCMS, 'site'],
  [['pc_rigid'], 'Cracking (depth measurement)', LCMS_R, 'site'],
  [['pc_rigid'], 'Cracking (length measurement)', LCMS_R, 'site'],
  [['pc_rigid'], 'Cracking (width measurement)', LCMS_R, 'site'],
  [['pc_rigid'], 'Pothole (area)', LCMS_R, 'site'],

  [['rcc'], 'Carbonation depth', 'IS 516 (Part 5/Sec 3)', 'site'],
  [['rcc'], 'Cover depth', 'BS 1881 (Part 204)', 'site'],
  [['rcc'], 'Crack depth', 'ASTM C597', 'site'],
  [['rcc'], 'Half-cell potential', 'IS 516 (Part 5/Sec 2)', 'site'],
  [['rcc'], 'Pile integrity test', 'IS 14893', 'site'],
  [['rcc'], 'Rebound hammer', 'IS 516 (Part 5/Sec 4)', 'site'],
  [['rcc'], 'Ultrasonic pulse velocity (UPV)', 'IS 516 (Part 5/Sec 1)', 'site'],
].map(([materials, test, clause, a, b]) => {
  // 4th/5th slot: a finished-test id and/or the 'site' flag, in either order
  const flags = [a, b].filter(Boolean);
  return { materials, test, clause, site: flags.includes('site'), done: flags.find((f) => f !== 'site') || null };
});

// Pending (not yet built) tests for one material, in scope order. Finished tests are looked up by id in testRegistry.js.
export const pendingFor = (materialId) => ROWS.filter((r) => r.materials.includes(materialId) && !r.done);
// Ids of the finished tests that the scope allows for this material.
export const doneIdsFor = (materialId) => [...new Set(ROWS.filter((r) => r.materials.includes(materialId) && r.done).map((r) => r.done))];
